import type { ReactElement } from 'react';
import { Check, CloudOff, Loader2, WifiOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/cn';
import type { TrustAutosaveStatus } from '../../../lib/trust/trustAutosaveTypes';

/** @deprecated Use TrustAutosaveStatus — kept for studio imports. */
export type AutosaveStatus = TrustAutosaveStatus;

type AutosaveIndicatorProps = {
  status: TrustAutosaveStatus;
  className?: string;
  /** Subtle top bar always visible; chip mode hides when idle. */
  variant?: 'chip' | 'bar';
};

const BAR_TONE: Record<TrustAutosaveStatus, string> = {
  idle: 'bg-slate-300/70 dark:bg-slate-600/50',
  saving: 'bg-dg-accent-grade/70 dark:bg-amber-500/60',
  saved: 'bg-emerald-500/75 dark:bg-emerald-500/55',
  error: 'bg-red-500/80 dark:bg-red-500/65',
  offline: 'bg-slate-500/65 dark:bg-slate-400/50',
};

export function AutosaveIndicator({
  status,
  className,
  variant = 'chip',
}: AutosaveIndicatorProps): ReactElement | null {
  const { t } = useTranslation();

  if (variant === 'bar') {
    return (
      <div
        className={cn('relative h-0.5 w-full shrink-0 overflow-hidden', className)}
        aria-hidden={status === 'idle'}
      >
        <div
          className={cn(
            'h-full w-full transition-[opacity,background-color] duration-500 ease-out motion-reduce:transition-none',
            BAR_TONE[status],
            status === 'idle' && 'opacity-40',
            status === 'saving' && 'opacity-100 animate-pulse motion-reduce:animate-none',
            status !== 'idle' && status !== 'saving' && 'opacity-100'
          )}
        />
        <span className="sr-only">{labelForStatus(status, t)}</span>
      </div>
    );
  }

  if (status === 'idle') return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'inline-flex items-center gap-1.5 rounded-dg-control border px-2.5 py-1 text-dg-secondary transition-colors motion-reduce:transition-none',
        status === 'saving' &&
          'border-slate-200/80 bg-white/90 text-slate-600 dark:border-slate-600 dark:bg-slate-900/50 dark:text-slate-300',
        status === 'saved' &&
          'border-dg-accent-grade/25 bg-dg-accent-gradeLight/80 text-amber-900 dark:border-amber-800/40 dark:bg-amber-950/30 dark:text-amber-200',
        status === 'offline' &&
          'border-slate-300/70 bg-slate-50/95 text-slate-700 dark:border-slate-600 dark:bg-slate-800/50 dark:text-slate-200',
        status === 'error' &&
          'border-red-200/80 bg-red-50 text-red-700 dark:border-red-800/50 dark:bg-red-950/40 dark:text-red-300',
        className
      )}
    >
      <StatusIcon status={status} />
      <span className="font-medium">{labelForStatus(status, t)}</span>
    </div>
  );
}

function StatusIcon({ status }: { status: TrustAutosaveStatus }): ReactElement | null {
  if (status === 'saving') {
    return (
      <Loader2
        className="h-3.5 w-3.5 shrink-0 animate-spin motion-reduce:animate-none"
        aria-hidden
      />
    );
  }
  if (status === 'saved') {
    return (
      <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden />
    );
  }
  if (status === 'offline') {
    return <WifiOff className="h-3.5 w-3.5 shrink-0" aria-hidden />;
  }
  if (status === 'error') {
    return <CloudOff className="h-3.5 w-3.5 shrink-0" aria-hidden />;
  }
  return null;
}

function labelForStatus(status: TrustAutosaveStatus, t: (key: string) => string): string {
  switch (status) {
    case 'saving':
      return t('trust.autosave.saving');
    case 'saved':
      return t('trust.autosave.saved');
    case 'offline':
      return t('trust.autosave.offline');
    case 'error':
      return t('trust.autosave.error');
    default:
      return t('trust.autosave.idle');
  }
}
